import { useState, useContext, useEffect, useMemo, useRef } from "react";

//Components
import { FormControl, Modal, TextField, Button } from "@material-ui/core";
import Autocomplete, {
  createFilterOptions,
} from "@material-ui/lab/Autocomplete";
import { KeyboardDatePicker } from "@material-ui/pickers";
import PhoneInputRefactor from "./PhoneInputRefactor";
import AddressInputAutoSuggest from "./AddressInputAutoSuggest";

//Fetching
import { useLazyQuery, useMutation } from "@apollo/client";
import { PERSON_AUTO_SUGGEST } from "../Queries/PersonAutoSuggest";

//Styling
import { useStyles } from "../Styles/styles";

//Utils
import { formatName, handleUuid, personAlertColor } from "../utils/utils";
import { UserContext } from "../Contexts/UserContext";
import { shouldBeLocked } from "../utils/Roles";
import { GET_PERSON } from "../Queries/GetPerson";
import { ADD_OR_UPDATE_PERSON } from "../Mutations/AddOrUpdatePerson";
import { throttleFunction } from "../utils/throttleFunction";

import moment from "moment";

const currentPersonInitialState = person => {
  return {
    lastName: person?.personalAttribute?.lastName,
    firstName: person?.personalAttribute?.firstName,
    middleName: person?.personalAttribute?.middleName,
    suffix: person?.personalAttribute?.suffix,
    organization: person?.personalAttribute?.organization || false,
    dob: person?.personalAttribute?.dob || "",
    phone: person?.personalAttribute?.phone,
    homeAddress: {
      name: person?.personalAttribute?.homeAddress?.name,
    },
  };
};

const newPersonInitialState = business => {
  return {
    lastName: "",
    firstName: "",
    middleName: "",
    suffix: "",
    organization: business,
    dob: null,
    phone: null,
    homeAddress: {
      name: "",
    },
  };
};

const PersonAutoSuggestRefactor = ({
  person,
  business = false,
  userRoles,
  userId,
  dispatch,
  action,
  handleAddPerson,
  incidentId,
  containerStyle = {},
  setPausePolling, // ensure this is passed as a prop
  mergePersonAction,
  inIncident,
  ...props
}) => {
  const memoizedLocked = useMemo(
    () => shouldBeLocked(undefined, props?.canEdit, userRoles),
    [props?.canEdit, userRoles],
  );
  const [showNameModal, setShowNameModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  const filter = createFilterOptions();
  const classes = useStyles();
  const { responseErrorHandler, onlineState } = useContext(UserContext);

  const [personAutoSuggest] = useLazyQuery(PERSON_AUTO_SUGGEST, {
    variables: { originatorId: userId },
    onError: error => {
      console.error(JSON.stringify(error));
    },
    onCompleted: data => {
      if (data) {
        responseErrorHandler(data);
        setPersonOptions(data.personAutoSuggest);
      }
    },
  });

  const [addPersonModal, setAddPersonModal] = useState(false);
  const [searchValue, setSearchValue] = useState(null);
  const [personOptions, setPersonOptions] = useState([]);

  const [currentPerson, setCurrentPerson] = useState(
    currentPersonInitialState(person),
  );

  useEffect(() => {
    setCurrentPerson(prevState => {
      return { ...prevState, organization: business };
    });
  }, [business]);

  useEffect(() => {
    if (searchValue && searchValue.length > 2 && onlineState) {
      personAutoSuggest({ variables: { input: searchValue, count: 20 } });
    } else {
      setPersonOptions([]);
    }
  }, [searchValue, personAutoSuggest, onlineState]);

  const [getPerson] = useLazyQuery(GET_PERSON, {
    variables: { originatorId: userId },
    onError: error => {
      console.error(JSON.stringify(error));
    },
    onCompleted: data => {
      if (data) {
        responseErrorHandler(data);
        //Passes the person to the parent
        handleAddPerson(data.getPerson);
      }
    },
  });

  const handleAddMatchToIncident = match => {
    //person is added in the onCompleted of getPerson
    getPerson({ variables: { personId: match?.id } });
  };

  const handleNameChange = event => {
    let name = event.target.name || null;
    let value = event.target.value;
    if (!name) {
      alert(
        `Please add a name field that matches the state name to the component that was just updated`,
      );
      return null;
    }
    setCurrentPerson(prevState => {
      return { ...prevState, [name]: value };
    });
  };

  const handleCurrentPersonDateChange = (value, name, date) => {
    setCurrentPerson(prevState => {
      return { ...prevState, [name]: date };
    });
  };

  const handleNewPersonPhoneChange = (phoneNumber, propertyName) => {
    setNewPerson(prevState => {
      return { ...prevState, [propertyName]: phoneNumber };
    });
  };
  const handleCurrentPersonPhoneChange = (phoneNumber, propertyName) => {
    setCurrentPerson(prevState => {
      return { ...prevState, [propertyName]: phoneNumber };
    });
  };
  const [updatePerson] = useMutation(ADD_OR_UPDATE_PERSON, {
    context: { serializationKey: "incidentQueue" },
  });

  const handleEditPerson = () => {
    dispatch({
      type: action,
      payload: { person: { personalAttribute: { ...currentPerson } } },
    });
    handleEditModalClose();
    const input = {
      originatorId: userId,
      personId: person.id,
      firstName: currentPerson.firstName,
      lastName: currentPerson.lastName,
      middleName: currentPerson.middleName,
      suffix: currentPerson.suffix,
      incidentId: incidentId ? incidentId : null,
    };

    updatePerson({
      variables: { input },
      context: {
        serializationKey: "incidentQueue",
      },
    });
  };

  const updateCurrentPersonAddress = address => {
    let localAddress = { ...address };
    localAddress.addressName = address?.name;
    setNewPerson(prevState => {
      return { ...prevState, homeAddress: localAddress };
    });
  };

  const handleEditModalClose = () => {
    setShowEditModal(false);
    setCurrentPerson(currentPersonInitialState(person));
  };

  const [newPerson, setNewPerson] = useState(() =>
    newPersonInitialState(business),
  );
  const containerRef = useRef(null);

  useEffect(() => {
    if (!setPausePolling) return;
    const node = containerRef.current;
    const handleFocusIn = () => {
      setPausePolling(true);
    };
    const handleFocusOut = () => {
      setPausePolling(false);
    };
    if (node) {
      node.addEventListener("focusin", handleFocusIn);
      node.addEventListener("focusout", handleFocusOut);
    }
    return () => {
      if (node) {
        node.removeEventListener("focusin", handleFocusIn);
        node.removeEventListener("focusout", handleFocusOut);
      }
    };
  }, [setPausePolling]);

  return (
    <div ref={containerRef} style={containerStyle}>
      {!showNameModal ? (
        <FormControl className={""} style={{ flexGrow: 1 }}>
          <TextField
            inputProps={{
              style: { color: personAlertColor(person, true, false) },
            }}
            InputLabelProps={{ shrink: true }}
            id={"auto-suggest-person-name-" + handleUuid()}
            label={business ? "Business Name" : "Name"}
            disabled={!props?.canEdit}
            value={
              person?.personalAttribute?.organization
                ? person?.personalAttribute?.lastName
                  ? person?.personalAttribute?.lastName
                  : ""
                : formatName(
                    person?.personalAttribute?.firstName,
                    person?.personalAttribute?.middleName,
                    person?.personalAttribute?.lastName,
                  )
            }
            onClick={
              !memoizedLocked
                ? () =>
                    person?.id && person?.personalAttribute?.lastName
                      ? setShowEditModal(true)
                      : setShowNameModal(true)
                : null
            }
            onFocus={
              !memoizedLocked
                ? () =>
                    person?.id && person?.personalAttribute?.lastName
                      ? setShowEditModal(true)
                      : setShowNameModal(true)
                : null
            }
          />
        </FormControl>
      ) : (
        <Autocomplete
          value={person}
          //Add a new person on blur if there's any text
          // let nameSplit = newValue.inputValue.split(' ')
          //   nameSplit[0] = nameSplit[0].replace(/,\s*$/, "");
          //   setNewPerson(prevState => {
          //     return { ...prevState, lastName: nameSplit[0]?.trim() || "", firstName: nameSplit[1]?.trim() || "", middleName: nameSplit[2]?.trim() || "" }
          //   })
          onBlur={() => setShowNameModal(false)}
          autoHighlight
          autoSelect
          disabled={memoizedLocked}
          onInputChange={(event, newValue) => setSearchValue(newValue)}
          onChange={(event, newValue) => {
            if (
              typeof newValue === "object" &&
              newValue &&
              newValue.action !== "Add"
            ) {
              setShowNameModal(false);
              handleAddMatchToIncident(newValue);
            }
            if (newValue && newValue.action && newValue.action === "Add") {
              let nameSplit = newValue.inputValue.split(" ");
              nameSplit[0] = nameSplit[0].replace(/,\s*$/, "");
              setNewPerson(prevState => {
                if (business) {
                  return { ...prevState, lastName: newValue.inputValue || "" };
                } else {
                  return {
                    ...prevState,
                    lastName: nameSplit[0]?.trim() || "",
                    firstName: nameSplit[1]?.trim() || "",
                    middleName: nameSplit[2]?.trim() || "",
                  };
                }
              });
              setAddPersonModal(true);
            }
          }}
          filterOptions={(options, params) => {
            const filtered = filter(options, params);

            // Suggest the creation of a new value
            if (params.inputValue !== "") {
              filtered.push({
                inputValue: params.inputValue,
                value: `Add "${params.inputValue}"`,
                action: "Add",
              });
            }

            return filtered;
          }}
          selectOnFocus
          // clearOnBlur
          handleHomeEndKeys
          id="free-solo-with-text-demo"
          options={personOptions}
          getOptionLabel={option => {
            // Value selected with enter, right from the input
            if (typeof option === "string") {
              return option;
            }
            // Add "xxx" option created dynamically
            if (option.inputValue) {
              return option.inputValue;
            }
            // Option when starting with a person
            if (option.personalAttribute) {
              return formatName(
                option.personalAttribute.firstName,
                option.personalAttribute.middleName,
                option.personalAttribute.lastName,
              );
            }
            // Autosuggest option
            return option.value ? option.value : "";
          }}
          renderOption={option => option.value}
          style={{ width: 300 }}
          freeSolo
          renderInput={params => (
            // eslint-disable-next-line jsx-a11y/no-autofocus -- Focus the replacement input when name editing starts.
            <TextField autoFocus
              {...params}
              label={business ? "Business Name" : "Last, First Middle"}
              variant="outlined"
            />
          )}
        />
      )}
      {props.children}

      {/* Add person modal */}
      <CreatePersonModal
        addPersonModal={addPersonModal}
        setAddPersonModal={setAddPersonModal}
        // dispatch={dispatch}
        // mergePersonAction={mergePersonAction}
        handleAddPerson={handleAddPerson}
        business={business}
        memoizedLocked={memoizedLocked}
        personId={person?.id}
        personalAttributeId={person?.personalAttribute?.id}
        handlePhoneChange={handleNewPersonPhoneChange}
        newPerson={newPerson}
        setNewPerson={setNewPerson}
        userRoles={userRoles}
        userId={userId}
        setPausePolling={setPausePolling}
        {...props}
      />

      {/* Edit person modal */}
      <Modal
        // disableEnforceFocus
        open={showEditModal}
        onClose={handleEditModalClose}
        className={classes.modal}
        keepMounted={false}
      >
        <div className={classes.paper}>
          <h3>Edit a person</h3>
          <TextField
            id={"person-last-name-" + person?.id}
            label="Last Name"
            value={currentPerson.lastName || ""}
            name="lastName"
            onChange={handleNameChange}
            className={""}
            disabled={memoizedLocked}
          />
          <TextField
            id={"person-first-name-" + person?.id}
            label="First Name"
            value={currentPerson.firstName || ""}
            name="firstName"
            onChange={handleNameChange}
            className={""}
            disabled={memoizedLocked}
          />
          <TextField
            id={"person-middle-name-" + person?.id}
            label="Middle Name"
            value={currentPerson.middleName || ""}
            name="middleName"
            onChange={handleNameChange}
            className={""}
            disabled={memoizedLocked}
          />
          <TextField
            id={"person-suffix-" + person?.id}
            label="Suffix"
            value={currentPerson.suffix || ""}
            name="suffix"
            onChange={handleNameChange}
            className={""}
            disabled={memoizedLocked}
          />
          <FormControl
            className={classes.formControl}
            style={{ marginRight: "4vw" }}
          >
            <KeyboardDatePicker
              data-cy={"create-person-dob-picker-" + person?.id}
              inputProps={{
                "data-cy": "create-person-dob-picker-input-" + person?.id,
              }}
              clearable
              label="DOB"
              value={
                currentPerson.dob
                  ? moment
                      .utc(currentPerson.dob, "YYYY-MM-DD HH:mm:ss UTC")
                      .local()
                  : null
              }
              onChange={(date, value) =>
                handleCurrentPersonDateChange(value, "dob", date)
              }
              format="MM/DD/YYYY"
              style={{ width: "150px" }}
              disabled={memoizedLocked}
            />
          </FormControl>
          {/* <FormControl className={classes.formControl}> */}
          <PhoneInputRefactor
            setPhoneNumber={handleCurrentPersonPhoneChange}
            phoneString={currentPerson.phone || ""}
            personId={person?.id ? person?.id : person?.uuid}
            modalClass={classes.modal}
            inputClass={classes.modalInput}
            paperClass={classes.paper}
            title={"Phone"}
            propertyName={"phone"}
            textFieldClass={classes.smallMatchMargin}
            style={{ marginRight: "4vw" }}
            userRoles={userRoles}
            canEdit={props.canEdit}
          />
          {/* </FormControl> */}
          <AddressInputAutoSuggest
            address={currentPerson.homeAddress}
            id={person?.id || null}
            setAddress={updateCurrentPersonAddress}
            updateType={"person"}
            title={"Address"}
            userRoles={userRoles}
            {...props}
          />
          <Button onClick={!memoizedLocked ? () => handleEditPerson() : null}>
            Update {business ? "Business" : "Person"}
          </Button>
          <Button onClick={handleEditModalClose}>Cancel</Button>
        </div>
      </Modal>
    </div>
  );
};

const CreatePersonModal = ({
  addPersonModal,
  setAddPersonModal,
  business,
  memoizedLocked,
  personId,
  handlePhoneChange,
  newPerson,
  setNewPerson,
  userId,
  userRoles,
  personalAttributeId,
  dispatch,
  mergePersonAction,
  handleAddPerson,
  setPausePolling,
  ...props
}) => {
  const classes = useStyles();
  const [createPerson] = useMutation(ADD_OR_UPDATE_PERSON, {
    context: { serializationKey: "incidentQueue" },
  });

  const throttledCreatePerson = throttleFunction(createPerson);

  const handleNewPersonPersonChange = event => {
    let name = event.target.name || null;
    let value = event.target.value;
    if (!name) {
      alert(
        `Please add a name field that matches the state name to the component that was just updated`,
      );
      return null;
    }
    setNewPerson(prevState => {
      return { ...prevState, [name]: value };
    });
  };

  const handleDateChange = (value, name, date) => {
    setNewPerson(prevState => {
      return { ...prevState, [name]: date };
    });
  };

  const updateAddress = address => {
    let localAddress = { ...address };
    localAddress.addressName = address?.name;
    setNewPerson(prevState => {
      return { ...prevState, homeAddress: localAddress };
    });
  };

  const handleCreatePerson = () => {
    setAddPersonModal(false);
    // let personUuid = handleUuid()
    const input = {
      originatorId: userId,
      personId: personId,
      firstName: newPerson.firstName,
      lastName: newPerson.lastName,
      middleName: newPerson.middleName,
      suffix: newPerson.suffix,
      organization: newPerson.organization,
      dob: newPerson?.dob?.isValid()
        ? newPerson.dob.utc().format("YYYY-MM-DD HH:mm:ss UTC")
        : "",
      phone: newPerson.phone,
      homeAddressId: newPerson?.homeAddress?.id,
    };

    //change this to update the person
    let personForState = {
      id: personId,
      personalAttribute: {
        id: personalAttributeId,
        firstName: newPerson.firstName,
        lastName: newPerson.lastName,
        middleName: newPerson.middleName,
        suffix: newPerson.suffix,
        organization: newPerson.organization,
        dob: newPerson?.dob?.isValid()
          ? newPerson.dob.utc().format("YYYY-MM-DD HH:mm:ss UTC")
          : "",
        phone: newPerson.phone,
        homeAddress: {
          ...newPerson?.homeAddress,
          addressName: newPerson?.homeAddress?.name,
        },
      },
    };
    throttledCreatePerson({ variables: { input } }).then(res => {
      personForState.id = res.data.addOrUpdatePerson.person.id;
      personForState.personalAttribute.id =
        res.data.addOrUpdatePerson.person.personalAttribute.id;
      handleAddPerson(personForState);
    });
  };

  useEffect(() => {
    setNewPerson(prevState => {
      return { ...prevState, organization: business };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [business]);

  return (
    <Modal
      // disableEnforceFocus
      open={addPersonModal}
      onClose={() => setAddPersonModal(false)}
      className={classes.modal}
    >
      <div className={classes.paper}>
        <h3>Create a {business ? "business" : "person"}</h3>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-end",
          }}
        >
          <TextField
            id={"person-last-name-" + personId}
            label={business ? "Business Name" : "Last Name"}
            value={newPerson.lastName || ""}
            name="lastName"
            onChange={handleNewPersonPersonChange}
            disabled={memoizedLocked}
            // className={""}
          />
          {!business && (
            <>
              <TextField
                id={"person-first-name-" + personId}
                label="First Name"
                value={newPerson.firstName || ""}
                name="firstName"
                onChange={handleNewPersonPersonChange}
                disabled={memoizedLocked}
                // className={""}
              />
              <TextField
                id={"person-middle-name-" + personId}
                label="Middle Name"
                value={newPerson.middleName || ""}
                name="middleName"
                onChange={handleNewPersonPersonChange}
                disabled={memoizedLocked}
                // className={""}
              />
              <TextField
                id={"person-suffix-" + personId}
                label="Suffix"
                value={newPerson.suffix || ""}
                name="suffix"
                onChange={handleNewPersonPersonChange}
                disabled={memoizedLocked}
                // className={""}
              />
              <FormControl
                className={classes.formControl}
                style={{ marginRight: "4vw" }}
              >
                <KeyboardDatePicker
                  data-cy={"create-person-dob-picker-" + personId}
                  inputProps={{
                    "data-cy": "create-person-dob-picker-input-" + personId,
                  }}
                  clearable
                  label="DOB"
                  value={
                    newPerson.dob
                      ? moment
                          .utc(newPerson.dob, "YYYY-MM-DD HH:mm:ss UTC")
                          .local()
                      : null
                  }
                  onChange={(date, value) =>
                    handleDateChange(value, "dob", date)
                  }
                  format="MM/DD/YYYY"
                  style={{ width: "150px" }}
                  disabled={memoizedLocked}
                />
              </FormControl>
            </>
          )}
          <PhoneInputRefactor
            setPhoneNumber={handlePhoneChange}
            phoneString={newPerson.phone || ""}
            personId={personId}
            modalClass={classes.modal}
            inputClass={classes.modalInput}
            paperClass={classes.paper}
            title={"Phone"}
            propertyName={"phone"}
            textFieldClass={classes.smallMatchMargin}
            style={{ marginRight: "4vw" }}
            userRoles={userRoles}
            canEdit={props.canEdit}
          />
          <AddressInputAutoSuggest
            address={newPerson.homeAddress}
            id={personId || null}
            setAddress={updateAddress}
            updateType={"person"}
            title={"Address"}
            userRole={userRoles}
            pauseLocationPolling={() => setPausePolling(true)}
            resumeLocationPolling={() => setPausePolling(false)}
            // canEdit={props.canEdit}
            {...props}
          />
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <Button
            style={{ marginRight: "1vw" }}
            onClick={!memoizedLocked ? () => handleCreatePerson() : null}
          >
            Create {business ? "Business" : "Person"}
          </Button>
          <Button
            onClick={
              !memoizedLocked
                ? () => {
                    setNewPerson({
                      firstName: "",
                      middleName: "",
                      lastName: "",
                      suffix: "",
                      homeAddress: { name: "" },
                      organization: business,
                      dob: null,
                      phone: null,
                    });
                    setAddPersonModal(false);
                  }
                : null
            }
          >
            Cancel
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default PersonAutoSuggestRefactor;
